import { BaseFrontdoorAdapter } from "../frontdoor/BaseFrontdoorAdapter";

/**
 * FrontdoorUserAdapter
 * Manages platform-level User CRUD operations via the Frontdoor REST API.
 * Endpoints sourced from: api_blueprint.md > Users
 */
export class FrontdoorUserAdapter extends BaseFrontdoorAdapter {
  /**
   * Get all users.
   * GET /users
   */
  async getAll(params = {}) {
    // If pagination is explicitly requested (e.g. grid fetches), do a single page request
    if (params.pageNumber !== undefined || params.pageSize !== undefined || params.limit !== undefined) {
      return this.get("/users", params);
    }

    // Otherwise, fetch all pages iteratively until we get all items
    let allItems = [];
    let currentPage = 1;
    const pageSize = 99; // use a larger page size to minimize requests
    
    while (true) {
      const response = await this.get("/users", {
        ...params,
        pageNumber: currentPage,
        pageSize: pageSize,
      });

      if (!response) {
        break;
      }

      const items = response.items || response.data || [];
      allItems = allItems.concat(items);

      const responseTotalCount = response.totalCount ?? response.count ?? response.total ?? 0;
      if (items.length < pageSize || allItems.length >= responseTotalCount) {
        break;
      }

      currentPage++;
    }

    return {
      items: allItems,
      totalCount: allItems.length,
    };
  }

  /**
   * Create one or more users.
   * POST /users
   * @param {Object} payload - { users: [{ email, fullName, role }] }
   */
  async create(payload) {
    return this.post("/users", payload);
  }

  /**
   * Update a user by ID.
   * PUT /users/{id}
   * @param {string|number} id - User ID
   * @param {Object} payload - Fields to update
   */
  async update(id, payload) {
    return this.put(`/users/${id}`, payload);
  }

  /**
   * Get user by ID.
   * GET /users/{id}
   */
  async getById(id) {
    return this.get(`/users/${id}`);
  }

  /**
   * Get user by source ID.
   * GET /users/{sourceId}
   */
  async getBySourceId(sourceId) {
    return this.get(`/users/${sourceId}`);
  }

  /**
   * Soft delete a user.
   * DELETE /users/{id}
   */
  async delete(id) {
    return super.delete(`/users/${id}`);
  }

  /**
   * Update a user profile.
   * PUT /users/{id}/profile
   */
  async updateProfile(id, payload) {
    return this.put(`/users/${id}/profile`, payload);
  }

  /**
   * Call legacy ProfileImageUploadHandler and persist returned blob URLs.
   * POST /user-images/legacy/upload/{id}
   */
  async uploadProfileImage(id, formData) {
    return this.postMultipart(`/user-images/legacy/upload/${id}`, formData);
  }

  /**
   * Save image blob URLs from legacy handler callback.
   * POST /user-images/legacy/save
   */
  async saveImageUrls(payload) {
    return this.post(`/user-images/legacy/save`, payload);
  }

  /**
   * Change a user password.
   * POST /users-password/change/{id}
   */
  async changePassword(id, payload) {
    return this.post(`/users-password/change/${id}`);
  }

  /**
   * Resend user invitation.
   * POST /users/{id}/resend-invitation
   */
  async resendInvitation(id) {
    return this.post(`/users/${id}/resend-invitation`);
  }
}
